import zIndex from '@mui/material/styles/zIndex';
import { useState, useEffect, useRef } from 'react';
import { Line, Circle, Group, Text, Rect } from 'react-konva';

const PolygonAnnotation = ({
  points,
  flattenedPoints,
  size,
  isFinished,
  handlePointDragMove,
  handleGroupDragEnd,
  handleMouseOverStartPoint,
  handleMouseOutStartPoint,
  isSelected,
  onClick,
  label,
  isDragMode,
  color
}) => {
  const vertexRadius = 6;
  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const groupRef = useRef(null);

  useEffect(() => {
    if (groupRef.current) {
      const layer = groupRef.current.getLayer();
      if (isSelected) {
        groupRef.current.moveToTop();
        const siblings = layer.children.toArray();
        siblings.forEach(child => {
          if (child !== groupRef.current && child.name() === 'polygon') {
            child.moveToBottom();
          }
        });
      }
    }
  }, [isSelected]);

  const constrainPoint = (x, y, stage) => {
    if (!stage) return { x, y };

    // Get stage dimensions
    const stageWidth = stage.width();
    const stageHeight = stage.height();

    return {
      x: Math.max(0, Math.min(stageWidth, x)),
      y: Math.max(0, Math.min(stageHeight, y))
    };
  };

  const handleVertexDragMove = (e, index) => {
    if (!isDragMode) return;

    const circle = e.target;
    const stage = circle.getStage();
    const OFFSET = 10;

    // Constrain position before sending to handler
    const x = Math.min(Math.max(circle.x(), OFFSET), stage.width() - OFFSET);
    const y = Math.min(Math.max(circle.y(), OFFSET), stage.height() - OFFSET);

    // Set the circle position immediately to prevent visual detachment
    circle.position({ x, y });

    handlePointDragMove({
      index,
      point: [x, y]
    });
  };

  const getLabelPosition = () => {
    if (!points.length) return { x: 0, y: 0 };
    const xs = points.map(p => p[0]);
    const ys = points.map(p => p[1]);
    return {
      x: (Math.min(...xs) + Math.max(...xs)) / 2,
      y: (Math.min(...ys) + Math.max(...ys)) / 2
    };
  };

  const getBackgroundWidth = label => {
    const textWidth = label?.length * 8 || 0;
    const padding = 24;
    return textWidth + padding;
  };

  return (
    <Group
      name="polygon"
      draggable={false}
      onClick={onClick}
      transformsEnabled="none"
    >
      <Line
        points={flattenedPoints}
        stroke={isSelected ? '#00ff00' : color?.stroke}
        strokeWidth={3}
        closed={isFinished}
        fill={isSelected ? 'rgba(0,255,0,0.3)' : color?.fill}
      />

      {points.map((point, index) => {
        const isFirstPoint = index === 0;
        return (
          <Circle
            key={`vertex-${index}`}
            x={point[0]}
            y={point[1]}
            radius={vertexRadius}
            fill={isSelected ? '#00ff00' : color?.stroke}
            stroke={isSelected ? '#00ff00' : '#ffffff'}
            strokeWidth={2}
            draggable={isDragMode && isSelected}
            onDragStart={e => {
              e.target.getStage().container().style.cursor = 'grabbing';
              setIsDraggingPoint(true);
            }}
            onDragEnd={e => {
              e.target.getStage().container().style.cursor = isDragMode
                ? 'grab'
                : 'crosshair';
              setIsDraggingPoint(false);
            }}
            onMouseOver={e => {
              if (isDragMode && isSelected) {
                e.target.getStage().container().style.cursor = 'grab';
              }
            }}
            onMouseOut={e => {
              if (isDragMode && isSelected) {
                e.target.getStage().container().style.cursor = 'default';
              }
            }}
            onDragMove={e => handleVertexDragMove(e, index)}
            {...(isFirstPoint &&
              !isFinished && {
                hitStrokeWidth: 12,
                onMouseOver: handleMouseOverStartPoint,
                onMouseOut: handleMouseOutStartPoint
              })}
            // dragBoundFunc={pos => {
            //   const stage = pos.target?.getStage();
            //   if (!stage) return pos;

            //   const OFFSET = 10;
            //   return {
            //     x: Math.min(Math.max(pos.x, OFFSET), size.width - OFFSET),
            //     y: Math.min(Math.max(pos.y, OFFSET), size.height - OFFSET)
            //   };
            // }}
          />
        );
      })}

      {label && isFinished && (
        <>
          <Rect
            {...getLabelPosition()}
            width={getBackgroundWidth(label)}
            height={24}
            fill="#102633"
            opacity={0.9}
            cornerRadius={4}
            shadowColor="rgba(0,0,0,0.2)"
            shadowBlur={4}
            shadowOffset={{ x: 0, y: 1 }}
            offsetX={getBackgroundWidth(label) / 2}
            offsetY={12}
          />
          <Text
            {...getLabelPosition()}
            text={label}
            fontSize={14}
            fill="white"
            align="center"
            verticalAlign="middle"
            offsetX={getBackgroundWidth(label) / 3.3}
            offsetY={5}
          />
        </>
      )}
    </Group>
  );
};

export default PolygonAnnotation;
